import { useCallback } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import {
  showErrorToast,
  showNetworkErrorToast,
  showValidationErrorToast,
} from "../utils/error-toast";

type ErrorHandlerOptions = {
  /** Callback for retry action */
  onRetry?: () => void;
  /** Override the default error message */
  message?: string;
  /** Duration in milliseconds (default: 5000) */
  duration?: number;
};

type SimpleToastOptions = {
  /** Duration in milliseconds */
  duration?: number;
};

/**
 * Hook for unified error handling across the application.
 *
 * This hook provides a consistent API for showing error toasts
 * with proper translations and optional retry actions.
 *
 * @example
 * ```tsx
 * function MyComponent() {
 *   const { handleError, showError } = useErrorHandler();
 *
 *   const handleSubmit = async () => {
 *     try {
 *       await createTrip.mutateAsync(data);
 *       showSuccess("trips.createSuccess");
 *     } catch (error) {
 *       handleError(error, { onRetry: handleSubmit });
 *     }
 *   };
 * }
 * ```
 */
export function useErrorHandler() {
  const { t } = useTranslation();

  /**
   * Handle an error with appropriate toast notification.
   * Uses ApiError codes to provide meaningful messages and actions.
   */
  const handleError = useCallback(
    (error: unknown, options?: ErrorHandlerOptions) => {
      showErrorToast(error, t, options);
    },
    [t]
  );

  /**
   * Show a simple error toast with a translation key.
   * Use this for known error scenarios where you want a specific message.
   */
  const showError = useCallback(
    (key: string, fallback?: string, options?: SimpleToastOptions) => {
      toast.error(t(key, fallback ?? key), options);
    },
    [t]
  );

  /**
   * Show a success toast with a translation key.
   */
  const showSuccess = useCallback(
    (key: string, fallback?: string, options?: SimpleToastOptions) => {
      toast.success(t(key, fallback ?? key), options);
    },
    [t]
  );

  /**
   * Show a network error toast with optional retry action.
   */
  const showNetworkError = useCallback(
    (onRetry?: () => void) => {
      showNetworkErrorToast(t, onRetry);
    },
    [t]
  );

  /**
   * Show a validation error toast.
   */
  const showValidationError = useCallback(
    (field?: string) => {
      showValidationErrorToast(t, field);
    },
    [t]
  );

  /**
   * Wrap an async function with error handling.
   * If the function throws, the error will be shown as a toast.
   *
   * @example
   * ```tsx
   * const handleClick = withErrorHandling(async () => {
   *   await doSomething();
   * }, { onRetry: handleClick });
   * ```
   */
  const withErrorHandling = useCallback(
    <T extends (...args: unknown[]) => Promise<unknown>>(
      fn: T,
      options?: ErrorHandlerOptions
    ): ((...args: Parameters<T>) => Promise<void>) => {
      return async (...args: Parameters<T>) => {
        try {
          await fn(...args);
        } catch (error) {
          handleError(error, options);
        }
      };
    },
    [handleError]
  );

  return {
    handleError,
    showError,
    showSuccess,
    showNetworkError,
    showValidationError,
    withErrorHandling,
    /** Direct access to translation function for custom messages */
    t,
  };
}
